/**
 * Sprites "secas" (pixel original): a floating window centred with
 * `translate(-50%, -50%)` (or dragged to a fractional spot) sits on a
 * half pixel. When the browser composites that window as its own layer
 * (it overlaps a blurred backdrop, a canvas, an animation…), the GPU
 * resamples the whole layer with bilinear filtering and every sprite and
 * text inside comes out soft. The reference game's windows land on whole
 * pixels; here every window root is nudged (CSS `translate`, which adds to
 * its `transform`) so its top-left corner is on a whole device pixel.
 */
import { zoomOf } from './uiZoom.js';

const SNAP_SELECTOR = [
  '.hzw', '.hzj', '.modal-card', '.hzae', '.fw', '.prey-window', '.hza', '[role="dialog"]',
].join(',');

const applied = new WeakMap<HTMLElement, { x: number; y: number }>();

function snapOne(element: HTMLElement): void {
  const rect = element.getBoundingClientRect();
  if (rect.width === 0 && rect.height === 0) return;
  const dpr = window.devicePixelRatio || 1;
  const before = applied.get(element) ?? { x: 0, y: 0 };
  // The nudge is in the element's own CSS px: under the UI zoom (`zoom: var(--ui-zoom)`, below
  // 1240×780) it moves the box by nudge × zoom on screen. Reading it back as 1:1 made the store
  // jump between three spots every frame at those sizes (owner 23/10 #03: "a loja treme").
  const zoom = zoomOf(element);
  // Where the box would be without our nudge, in device pixels.
  const rawX = (rect.left - before.x * zoom) * dpr;
  const rawY = (rect.top - before.y * zoom) * dpr;
  const x = (Math.round(rawX) - rawX) / dpr / zoom;
  const y = (Math.round(rawY) - rawY) / dpr / zoom;
  if (Math.abs(x - before.x) < 0.01 && Math.abs(y - before.y) < 0.01) return;
  applied.set(element, { x, y });
  element.style.translate = x === 0 && y === 0 ? '' : `${x}px ${y}px`;
}

let queued = false;
function snapAll(): void {
  queued = false;
  for (const element of document.querySelectorAll<HTMLElement>(SNAP_SELECTOR)) snapOne(element);
}

function schedule(): void {
  if (queued) return;
  queued = true;
  requestAnimationFrame(snapAll);
}

/** Watch the page and keep every floating window on whole pixels. */
export function installPixelSnap(): void {
  if (typeof window === 'undefined' || typeof MutationObserver === 'undefined') return;
  new MutationObserver(schedule).observe(document.body, {
    subtree: true, childList: true, attributes: true, attributeFilter: ['style', 'class'],
  });
  window.addEventListener('resize', schedule);
  // Windows that change size without a mutation (content growing, fonts loading).
  window.setInterval(schedule, 1000);
  schedule();
}
